import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Check, Loader2, Receipt, ExternalLink, Activity, CreditCard, RotateCcw } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLAN_PERKS = {
  candidate_premium: ["AI Resume Optimization", "Salary Insights", "Priority visibility", "AI career coach"],
  employer_pro: ["Unlimited AI matching", "Unlimited Recruiter Copilot", "AI screening", "Interview automation"],
};

const fmtAmount = (a, c) => {
  const cur = (c || "usd").toLowerCase();
  const n = Number(a).toLocaleString();
  return cur === "usd" ? `$${n}` : `${cur.toUpperCase()} ${n}`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function Account() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [history, setHistory] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [config, setConfig] = useState({ recurring: false });
  const [portalLoading, setPortalLoading] = useState(false);

  const plan = user?.plan;
  const perks = plan ? PLAN_PERKS[plan] || [] : [];
  const scheduledCancel = !!user?.cancel_at_period_end;
  const periodEnd = user?.current_period_end ? new Date(user.current_period_end * 1000) : null;
  const [sub, setSub] = useState(null);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    api.get("/account/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/account/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const resumePlan = async () => {
    setResuming(true);
    try {
      await api.post("/account/resume");
      await refreshUser();
      const r = await api.get("/account/subscription");
      setSub(r.data);
      toast.success("Your plan will renew again — welcome back!");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setResuming(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/account/cancel");
      await refreshUser();
      toast.success(config.recurring ? "Renewal cancelled — active until period end" : "Subscription cancelled");
      setConfirm(false);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCancelling(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/account/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setPortalLoading(false);
    }
  };

  return (
    <main data-testid="account-page" className="max-w-4xl mx-auto px-5 py-14">
      <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-3">Account</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{user?.name}</h1>
      <p className="text-sm text-[#4B5563] mb-10">{user?.email} · <span className="uppercase font-mono2 text-xs">{user?.role}</span></p>

      {/* Current plan */}
      <section className="border border-[#0A0A0A] p-7 mb-6" data-testid="current-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-1">Current plan</div>
            <div className="font-display font-black text-2xl tracking-tighter flex items-center gap-2">
              {plan ? (
                <>
                  <Crown size={20} className="text-[#002FA7]" /> {user.plan_name}
                </>
              ) : (
                "Free"
              )}
            </div>
            <div className="font-mono2 text-sm text-[#4B5563] mt-1" data-testid="plan-price">
              {plan === "candidate_premium" ? "$19 / month" : plan === "employer_pro" ? "$299 / month" : "$0 / forever"}
            </div>
          </div>
          {plan ? (
            scheduledCancel ? (
              <span data-testid="status-cancelling" className="font-mono2 text-xs uppercase border border-[#FF3B30] text-[#FF3B30] px-3 py-1.5">● Cancels at period end</span>
            ) : (
              <span data-testid="status-active" className="font-mono2 text-xs uppercase border border-[#10B981] text-[#10B981] px-3 py-1.5">● Active</span>
            )
          ) : (
            <span className="font-mono2 text-xs uppercase border border-[#E5E7EB] text-[#9CA3AF] px-3 py-1.5">Free tier</span>
          )}
        </div>

        {scheduledCancel && periodEnd && (
          <div data-testid="cancel-scheduled-note" className="mt-4 border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-4 py-2 text-sm">
            Your plan will not renew. You keep premium access until <b>{periodEnd.toLocaleDateString()}</b>.
          </div>
        )}

        {perks.length > 0 && (
          <ul className="grid sm:grid-cols-2 gap-2 mt-6">
            {perks.map((p) => (
              <li key={p} className="flex gap-2 text-sm"><Check size={16} className="text-[#002FA7] shrink-0 mt-0.5" />{p}</li>
            ))}
          </ul>
        )}

        <div className="mt-7 flex flex-wrap gap-3">
          {plan ? (
            scheduledCancel ? (
              <span className="font-mono2 text-xs text-[#9CA3AF]">Renewal already cancelled.</span>
            ) : confirm ? (
              <div className="flex items-center gap-3 flex-wrap" data-testid="cancel-confirm">
                <span className="text-sm text-[#4B5563]">{config.recurring ? "Stop auto-renewal? You keep access until the period ends." : "Cancel and lose premium perks immediately?"}</span>
                <button data-testid="cancel-confirm-yes" onClick={cancel} disabled={cancelling} className="bg-[#FF3B30] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                  {cancelling && <Loader2 size={15} className="animate-spin" />} Yes, cancel
                </button>
                <button data-testid="cancel-confirm-no" onClick={() => setConfirm(false)} className="btn-outline px-4 py-2 text-sm font-medium">Keep plan</button>
              </div>
            ) : (
              <button data-testid="cancel-plan-btn" onClick={() => setConfirm(true)} className="btn-outline px-5 py-2.5 text-sm font-medium">
                {config.recurring ? "Cancel auto-renewal" : "Cancel subscription"}
              </button>
            )
          ) : (
            <Link to="/pricing" data-testid="account-upgrade-btn" className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
              <Crown size={16} /> Upgrade plan
            </Link>
          )}
          <Link to="/pricing" className="btn-outline px-5 py-2.5 text-sm font-medium">View all plans</Link>
          {config.recurring && user?.has_billing_account && (
            <button data-testid="manage-billing-btn" onClick={openPortal} disabled={portalLoading} className="btn-outline px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              {portalLoading ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
              Manage billing & invoices
            </button>
          )}
        </div>
        <div data-testid="billing-mode" className="mt-4 font-mono2 text-xs text-[#9CA3AF]">
          {config.recurring ? "Recurring billing via Stripe · auto-renews monthly" : "Test checkout (one-time) · recurring auto-renewal activates with a live Stripe key"}
        </div>
      </section>

      {/* Subscription health */}
      {plan && (
        <section className="border border-[#0A0A0A] mb-6" data-testid="subscription-health">
          <div className="flex items-center gap-2 p-5 border-b border-[#E5E7EB]">
            <Activity size={16} className="text-[#002FA7]" />
            <h2 className="font-display font-bold text-lg">Subscription health</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-px bg-[#E5E7EB]">
            <div className="bg-white p-5">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-1">{sub?.cancel_at_period_end ? "Access until" : "Next renewal"}</div>
              <div data-testid="sub-renewal" className="font-display font-bold text-lg">
                {sub?.current_period_end ? new Date(sub.current_period_end * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
              </div>
              {sub?.cancel_at_period_end && <div className="font-mono2 text-[11px] text-[#FF3B30] mt-1">Will not renew</div>}
            </div>
            <div className="bg-white p-5">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-1">Card on file</div>
              <div data-testid="sub-card" className="font-display font-bold text-lg flex items-center gap-2">
                <CreditCard size={16} className="text-[#002FA7]" />
                {sub?.card_last4 ? `${(sub.card_brand || "card").toUpperCase()} •••• ${sub.card_last4}` : "—"}
              </div>
              {!sub?.card_last4 && config.recurring && user?.has_billing_account && (
                <div className="font-mono2 text-[11px] text-[#9CA3AF] mt-1">Manage in billing portal</div>
              )}
            </div>
            <div className="bg-white p-5">
              <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-1">Status</div>
              <div data-testid="sub-status" className={`font-display font-bold text-lg capitalize ${sub?.status === "past_due" ? "text-[#FF3B30]" : "text-[#10B981]"}`}>
                {sub?.status || "active"}
              </div>
            </div>
          </div>
          {sub?.cancel_at_period_end && config.recurring && (
            <div className="p-5 border-t border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm text-[#4B5563]">Changed your mind? Keep your plan active and resume auto-renewal.</span>
              <button data-testid="resume-plan-btn" onClick={resumePlan} disabled={resuming} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                {resuming ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                Resume plan
              </button>
            </div>
          )}
        </section>
      )}

      {/* Billing history */}
      <section className="border border-[#0A0A0A]" data-testid="billing-history">
        <div className="flex items-center gap-2 p-5 border-b border-[#E5E7EB]">
          <Receipt size={16} className="text-[#002FA7]" />
          <h2 className="font-display font-bold text-lg">Billing history</h2>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#9CA3AF] font-mono2" data-testid="history-empty">No transactions yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono2 text-xs uppercase text-[#4B5563] text-left">
                <th className="p-4 font-medium">Date</th>
                <th className="p-4 font-medium">Plan</th>
                <th className="p-4 font-medium">Amount</th>
                <th className="p-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((t) => (
                <tr key={t.session_id} className="border-t border-[#E5E7EB]">
                  <td className="p-4">{fmtDate(t.created_at)}</td>
                  <td className="p-4">{t.plan_name}</td>
                  <td className="p-4 font-mono2">{fmtAmount(t.amount, t.currency)}</td>
                  <td className="p-4">
                    <span className={`font-mono2 text-xs uppercase ${t.payment_status === "paid" ? "text-[#10B981]" : "text-[#9CA3AF]"}`}>
                      {t.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
